import { create } from 'zustand';
import type {
  ColumnMap,
  MappingProfileDto,
  UploadBatchDto,
  ValidationResultDto,
} from '@ecom/contracts';
import { useUiStore } from '@/i18n';
import { translate } from '@/i18n/messages';
import { adminApi, apiBaseUrl, errorMessage, withSession } from '@/lib/api-client';
import { saveFile } from '@/lib/download';
import { getAccessToken } from '@/lib/session';

/**
 * The bulk import (ADR-014).
 *
 * Four deliberate steps — upload, map, dry run, import — so nothing reaches the
 * catalogue before an operator has seen which rows would fail. This store keeps
 * the batch and walks it forward; the screen never skips a step, because the
 * whole point of the pipeline is that a bad file is caught before it lands.
 */

export type Step = 'upload' | 'map' | 'review' | 'running' | 'done';

/** What a file holds: products, or customer accounts (ADR-070). */
export type ImportTarget = 'PRODUCTS' | 'CUSTOMERS';

/** Statuses that mean the batch has stopped, whatever the outcome. */
const TERMINAL: UploadBatchDto['status'][] = ['DONE', 'FAILED', 'CANCELLED'];

interface IngestionState {
  batch: UploadBatchDto | null;
  /**
   * The dry run's own result.
   *
   * Kept rather than re-read from the batch: the API deliberately zeroes the
   * batch's counters on validate so a re-run cannot show a previous run's
   * totals, which means the batch is the wrong place to look for them.
   */
  validation: ValidationResultDto | null;
  fields: string[];
  /** What the next file holds; a batch carries its own once uploaded. */
  target: ImportTarget;
  profiles: MappingProfileDto[];
  history: UploadBatchDto[];
  columnMap: ColumnMap;

  step: Step;
  busy: boolean;
  error: string | null;
  /** The batch whose error report is on its way down, if any. */
  downloading: string | null;

  loadReference: () => Promise<void>;
  setTarget: (target: ImportTarget) => Promise<void>;
  upload: (file: File) => Promise<boolean>;
  setColumn: (column: string, field: string) => void;
  /** Takes a saved mapping's columns; the name finds one beyond the first page the store holds. */
  applyProfile: (profileId: string, name?: string) => Promise<void>;
  /** Removes a saved mapping; the columns chosen on screen stay as they are. */
  deleteProfile: (profileId: string) => Promise<boolean>;
  validate: () => Promise<boolean>;
  run: () => Promise<boolean>;
  cancel: () => Promise<void>;
  saveProfile: (name: string) => Promise<boolean>;
  reset: () => void;
  /** Opens the server-sent progress stream; returns a cleanup function. */
  watch: () => () => void;
  /**
   * Saves a batch's error report — the one on screen, or one from the history.
   * Fetched through the SDK, which carries the session: a plain link to the
   * API has no token and only ever downloaded a 401.
   */
  downloadErrorReport: (batchId: string) => Promise<void>;
}

export const useIngestionStore = create<IngestionState>((set, get) => ({
  batch: null,
  validation: null,
  fields: [],
  target: 'PRODUCTS',
  profiles: [],
  history: [],
  columnMap: {},

  step: 'upload',
  busy: false,
  error: null,
  downloading: null,

  async loadReference() {
    const [fields, profiles, history] = await Promise.allSettled([
      withSession(() => adminApi.ingestion.fields(get().target)),
      withSession(() => adminApi.ingestion.profiles()),
      withSession(() => adminApi.ingestion.uploads({ limit: 10 })),
    ]);

    set({
      fields: fields.status === 'fulfilled' ? fields.value.fields : [],
      profiles: profiles.status === 'fulfilled' ? profiles.value.items : [],
      history: history.status === 'fulfilled' ? history.value.items : [],
    });
  },

  async setTarget(target) {
    set({ target });
    try {
      const { fields } = await withSession(() => adminApi.ingestion.fields(target));
      set({ fields });
    } catch (error) {
      set({ error: errorMessage(error, translate('genericError', useUiStore.getState().locale)) });
    }
  },

  async upload(file) {
    set({ busy: true, error: null });

    try {
      const created = await withSession(() => adminApi.ingestion.upload(file, get().target));

      set({
        batch: created.batch,
        // The API suggests a mapping from the header row; an operator corrects
        // it rather than building one from nothing.
        columnMap: created.suggestedColumnMap ?? {},
        step: 'map',
        busy: false,
      });
      return true;
    } catch (error) {
      set({ busy: false, error: errorMessage(error, 'Could not read that file.') });
      return false;
    }
  },

  setColumn(column, field) {
    set((state) => ({ columnMap: { ...state.columnMap, [column]: field as ColumnMap[string] } }));
  },

  async applyProfile(profileId, name) {
    let profile = get().profiles.find((entry) => entry.id === profileId);

    // The picker searches every saved mapping; the store holds only the first
    // page of them, so one found further down is looked up by its name.
    if (!profile && name) {
      try {
        const page = await withSession(() => adminApi.ingestion.profiles({ search: name }));
        profile = page.items.find((entry) => entry.id === profileId);
      } catch (error) {
        set({ error: errorMessage(error, translate('genericError', useUiStore.getState().locale)) });
      }
    }

    if (profile) set({ columnMap: profile.columnMap as ColumnMap });
  },

  async deleteProfile(profileId) {
    set({ busy: true, error: null });

    try {
      await withSession(() => adminApi.ingestion.deleteProfile(profileId));
      set((state) => ({ busy: false, profiles: state.profiles.filter((entry) => entry.id !== profileId) }));
      return true;
    } catch (error) {
      set({ busy: false, error: errorMessage(error, translate('genericError', useUiStore.getState().locale)) });
      return false;
    }
  },

  async validate() {
    const batch = get().batch;
    if (!batch) return false;

    set({ busy: true, error: null });

    try {
      // The dry run writes nothing — it reports which rows would fail and why.
      const result = await withSession(() =>
        adminApi.ingestion.validate(batch.id, { columnMap: get().columnMap }),
      );

      set({ batch: result.batch, validation: result, step: 'review', busy: false });
      return true;
    } catch (error) {
      set({ busy: false, error: errorMessage(error, 'Could not validate the file.') });
      return false;
    }
  },

  async run() {
    const batch = get().batch;
    if (!batch) return false;

    set({ busy: true, error: null });

    try {
      await withSession(() => adminApi.ingestion.run(batch.id));
      set({ step: 'running', busy: false });
      return true;
    } catch (error) {
      set({ busy: false, error: errorMessage(error, 'Could not start the import.') });
      return false;
    }
  },

  async cancel() {
    const batch = get().batch;
    if (!batch) return;

    try {
      await withSession(() => adminApi.ingestion.cancel(batch.id));
      const updated = await withSession(() => adminApi.ingestion.batch(batch.id));
      set({ batch: updated, step: 'done' });
    } catch (error) {
      set({ error: errorMessage(error, 'Could not cancel the batch.') });
    }
  },

  async saveProfile(name) {
    set({ busy: true, error: null });

    try {
      await withSession(() =>
        adminApi.ingestion.createProfile({ name, columnMap: get().columnMap }),
      );
      const profiles = await withSession(() => adminApi.ingestion.profiles());

      set({ profiles: profiles.items, busy: false });
      return true;
    } catch (error) {
      set({ busy: false, error: errorMessage(error, 'Could not save the mapping.') });
      return false;
    }
  },

  reset() {
    set({ batch: null, validation: null, columnMap: {}, step: 'upload', error: null });
    void get().loadReference();
  },

  /**
   * Live progress.
   *
   * `EventSource` cannot carry an Authorization header, so this reads the
   * stream with fetch and parses the SSE frames itself. A dropped stream falls
   * back to polling the batch — a progress bar that stops moving is worse than
   * one that updates every few seconds.
   */
  watch() {
    const batch = get().batch;
    if (!batch) return () => {};

    const controller = new AbortController();
    let polling: ReturnType<typeof setInterval> | null = null;

    const finish = async () => {
      const updated = await withSession(() => adminApi.ingestion.batch(batch.id));
      set({ batch: updated, step: 'done' });
    };

    const startPolling = () => {
      polling ??= setInterval(() => {
        void withSession(() => adminApi.ingestion.batch(batch.id)).then((updated) => {
          set({ batch: updated });
          if (TERMINAL.includes(updated.status)) {
            set({ step: 'done' });
            if (polling) clearInterval(polling);
          }
        });
      }, 2000);
    };

    void (async () => {
      try {
        const response = await fetch(
          `${apiBaseUrl()}/api/admin/ingestion/uploads/${batch.id}/progress`,
          {
            headers: { Authorization: `Bearer ${getAccessToken() ?? ''}`, Accept: 'text/event-stream' },
            signal: controller.signal,
          },
        );

        if (!response.ok || !response.body) {
          startPolling();
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          // SSE frames are separated by a blank line.
          const frames = buffer.split('\n\n');
          buffer = frames.pop() ?? '';

          for (const frame of frames) {
            const line = frame.split('\n').find((entry) => entry.startsWith('data:'));
            if (!line) continue;

            try {
              const event = JSON.parse(line.slice(5).trim()) as Partial<UploadBatchDto>;
              set((state) => ({ batch: state.batch ? { ...state.batch, ...event } : state.batch }));

              // React to the status the event carries rather than waiting for
              // the stream to close: the server may hold it open, and a screen
              // that still says "importing" over a finished batch is a lie.
              if (event.status && TERMINAL.includes(event.status)) {
                controller.abort();
                await finish();
                return;
              }
            } catch {
              // A malformed frame is not worth tearing the stream down for.
            }
          }
        }

        await finish();
      } catch {
        if (!controller.signal.aborted) startPolling();
      }
    })();

    return () => {
      controller.abort();
      if (polling) clearInterval(polling);
    };
  },

  async downloadErrorReport(batchId) {
    if (get().downloading) return;
    set({ downloading: batchId, error: null });

    try {
      await saveFile(() => withSession(() => adminApi.ingestion.errorReport(batchId)), 'import-errors.csv');
    } catch (error) {
      set({ error: errorMessage(error, translate('downloadErrorsFailed', useUiStore.getState().locale)) });
    } finally {
      set({ downloading: null });
    }
  },
}));
